import React, { useState } from "react";
// TODO: Implement custom API client
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/shared/components/ui/sheet";
import { Search, Webhook, ArrowDownLeft, ArrowUpRight, CheckCircle2, XCircle, Clock } from "lucide-react";
import ResellerLayout from "@/shared/components/reseller/ResellerLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

const statusConfig = {
  received: { color: "bg-blue-100 text-blue-700" },
  processed: { color: "bg-emerald-100 text-emerald-700" },
  failed: { color: "bg-red-100 text-red-700" },
  invalid_signature: { color: "bg-red-100 text-red-700" },
};

export default function ResellerWebhooks() {
  const [search, setSearch] = useState("");
  const [directionFilter, setDirectionFilter] = useState("all");
  const [selectedLog, setSelectedLog] = useState(null);
  const { t } = useLanguage();
  const resellerName = "Asia Gaming Network";

  // TODO: Implement custom API calls
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["webhook-logs"],
    queryFn: async () => [], // TODO: Implement WebhookLog.list API call
  });

  const filteredLogs = logs.filter(log => {
    if (search && !log.webhook_id?.toLowerCase().includes(search.toLowerCase()) &&
      !log.transaction_id?.toLowerCase().includes(search.toLowerCase())) return false;
    if (directionFilter !== "all" && log.direction !== directionFilter) return false;
    return true;
  });

  return (
    <ResellerLayout currentPage="ResellerWebhooks" resellerName={resellerName}>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('webhookLogs')}</h1>
          <p className="text-slate-500">{filteredLogs.length} {t('logs')}</p>
        </div>

        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder={`${t('search')}...`} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
            </div>
            <Select value={directionFilter} onValueChange={setDirectionFilter}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder={t('direction')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('allDirection')}</SelectItem>
                <SelectItem value="inbound">{t('inbound')}</SelectItem>
                <SelectItem value="outbound">{t('outbound')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t('webhook')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden sm:table-cell">{t('event')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t('status')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden md:table-cell">{t('response')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden lg:table-cell">{t('date')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoading ? (
                  <tr><td colSpan={5} className="text-center py-12 text-slate-500">{t('loading')}</td></tr>
                ) : filteredLogs.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-12 text-slate-500">{t('noLogs')}</td></tr>
                ) : (
                  filteredLogs.map((log, i) => {
                    const status = statusConfig[log.status] || statusConfig.received;
                    return (
                      <motion.tr
                        key={log.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: i * 0.02 }}
                        onClick={() => setSelectedLog(log)}
                        className="hover:bg-slate-50 cursor-pointer"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${log.direction === "inbound" ? "bg-blue-100" : "bg-emerald-100"
                              }`}>
                              {log.direction === "inbound" ? (
                                <ArrowDownLeft className="h-4 w-4 text-blue-600" />
                              ) : (
                                <ArrowUpRight className="h-4 w-4 text-emerald-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-mono text-sm">{log.webhook_id?.slice(0, 12)}...</p>
                              <p className="text-xs text-slate-400">{log.source}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <Badge variant="outline">{log.event_type?.replace(/\./g, " ")}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`${status.color} border-0`}>{t(log.status)}</Badge>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className={log.response_status < 400 ? "text-emerald-600" : "text-red-600"}>
                            {log.response_status || "—"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500 hidden lg:table-cell">
                          {log.created_date ? format(new Date(log.created_date), "MMM d, HH:mm") : t('nA')}
                        </td>
                      </motion.tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <Sheet open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
            {selectedLog && (
              <>
                <SheetHeader className="pb-4 border-b">
                  <SheetTitle className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                      <Webhook className="h-5 w-5 text-violet-600" />
                    </div>
                    <div>
                      <p className="text-lg">{t('webhookLog')}</p>
                      <Badge className={statusConfig[selectedLog.status]?.color}>{t(selectedLog.status)}</Badge>
                    </div>
                  </SheetTitle>
                </SheetHeader>
                <div className="py-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">{t('direction')}</p>
                      <p className="font-medium capitalize">{t(selectedLog.direction)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{t('event')}</p>
                      <p className="font-medium">{selectedLog.event_type}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{t('source')}</p>
                      <p className="font-medium">{selectedLog.source}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{t('responseStatus')}</p>
                      <p className="font-medium">{selectedLog.response_status || "—"}</p>
                    </div>
                  </div>
                  {selectedLog.payload && (
                    <div>
                      <p className="text-sm text-slate-500 mb-2">{t('payload')}</p>
                      <pre className="bg-slate-900 text-slate-100 p-4 rounded-xl text-xs overflow-x-auto max-h-60">
                        {selectedLog.payload}
                      </pre>
                    </div>
                  )}
                  {selectedLog.response_body && (
                    <div>
                      <p className="text-sm text-slate-500 mb-2">{t('response')}</p>
                      <pre className="bg-slate-900 text-slate-100 p-4 rounded-xl text-xs overflow-x-auto max-h-40">
                        {selectedLog.response_body}
                      </pre>
                    </div>
                  )}
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </ResellerLayout>
  );
}